import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { QUERY_PARAM_BIRTH_CHART_OVERRIDE, STORAGE_KEY_BIRTH_CHART_OVERRIDE } from '../../const';
import AuthApi from '../../service/Api/authApi';

/**
 * Server-driven feature flags (HORO-10360).
 *
 * One backend env var (`BIRTH_CHART_VEDA`) drives both repos. Signed-in users
 * also get `features.birthChart` on `GET /user`; anonymous visitors never load
 * that, so the public `GET /features` covers them. `selectBirthChartEnabled`
 * merges the two and stays false until an answer arrives, so the new UI never
 * flashes on and then off.
 */
const initialState = {
    loaded: false,
    birthChart: false
};

// Captured once at module load.
const _initialBirthChartQuery = (() => {
    if (typeof window === 'undefined') return false;
    try {
        return new URLSearchParams(window.location.search).get(QUERY_PARAM_BIRTH_CHART_OVERRIDE) === '1';
    } catch {
        return false;
    }
})();

export const fetchFeatures = createAsyncThunk('features/fetchFeatures', async (_, { rejectWithValue }) => {
    try {
        const response = await AuthApi.getFeatures();
        return response?.data || {};
    } catch (error) {
        return rejectWithValue(error?.message);
    }
});

const featuresSlice = createSlice({
    name: 'features',
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder
            .addCase(fetchFeatures.fulfilled, (state, { payload }) => {
                state.loaded = true;
                state.birthChart = !!payload?.birthChart;
            })
            // A failed fetch leaves the flag off — the safe direction.
            .addCase(fetchFeatures.rejected, (state) => {
                state.loaded = true;
            });
    }
});

// Dev QA only: force the UI on. Never honoured in a production build, and the
// backend endpoints still follow the env flag either way.
const devOverrideOn = () => {
    if (process.env.NODE_ENV === 'production') return false;
    if (_initialBirthChartQuery) return true;
    try {
        return typeof window !== 'undefined' && window.localStorage.getItem(STORAGE_KEY_BIRTH_CHART_OVERRIDE) === '1';
    } catch {
        return false;
    }
};

export const selectBirthChartEnabled = (state) => devOverrideOn() || !!state.features.birthChart || !!state.auth.user?.features?.birthChart;

export const featuresReducer = featuresSlice.reducer;
